import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { Movie } from '@/mocks/movies';
import { movieGenres, regions, sortOptions, tvGenres } from '@/mocks/movies';
import { filterMovies, getYears } from '@/lib/catalog';
import MovieCard from '@/components/feature/MovieCard';
import Pagination from '@/components/base/Pagination';
import EmptyState from '@/components/base/EmptyState';
import Breadcrumb from '@/components/base/Breadcrumb';
import FilterBar, { type FilterGroupConfig } from './FilterBar';

interface CategoryViewProps {
  type: 'movie' | 'tv';
  title: string;
  description: string;
  bannerImage: string;
}

const PAGE_SIZE = 8;
const ALL = '全部';
const episodeOptions = [ALL, '12以下', '21-30', '30以上'];

export default function CategoryView({ type, title, description, bannerImage }: CategoryViewProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialGenre = searchParams.get('genre') || ALL;

  const [genre, setGenre] = useState(initialGenre);
  const [year, setYear] = useState(ALL);
  const [region, setRegion] = useState(ALL);
  const [episodes, setEpisodes] = useState(ALL);
  const [sort, setSort] = useState('热门');
  const [page, setPage] = useState(1);
  const [panelOpen, setPanelOpen] = useState(false);

  const yearOptions = useMemo(
    () => [ALL, ...getYears(type).map((y) => String(y))],
    [type],
  );

  useEffect(() => {
    setPage(1);
  }, [genre, year, region, episodes, sort]);

  const results = useMemo<Movie[]>(
    () => filterMovies({ type, genre, year, region, episodes, sort }),
    [type, genre, year, region, episodes, sort],
  );

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = results.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const genres = type === 'movie' ? movieGenres : tvGenres;

  const groups: FilterGroupConfig[] = [
    { key: 'genre', label: '类型', options: genres, value: genre, onChange: setGenre },
    { key: 'year', label: '年份', options: yearOptions, value: year, onChange: setYear },
    { key: 'region', label: '地区', options: regions, value: region, onChange: setRegion },
  ];
  if (type === 'tv') {
    groups.push({
      key: 'episodes',
      label: '集数',
      options: episodeOptions,
      value: episodes,
      onChange: setEpisodes,
    });
  }
  groups.push({ key: 'sort', label: '排序', options: sortOptions, value: sort, onChange: setSort });

  const handleReset = () => {
    setGenre(ALL);
    setYear(ALL);
    setRegion(ALL);
    setEpisodes(ALL);
    setSort('热门');
    setSearchParams({});
  };

  return (
    <div className="w-full">
      <div className="relative h-[200px] w-full overflow-hidden bg-primary-950 md:h-[260px]">
        <img
          src={bannerImage}
          alt={`${title} 分类页头图`}
          title={`${title} 分类`}
          className="absolute inset-0 h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950/95 via-primary-950/60 to-primary-950/30" />
        <div className="absolute inset-x-0 bottom-0 mx-auto max-w-7xl px-4 pb-6 md:px-8 md:pb-8">
          <h1 className="font-heading text-2xl font-black text-background-50 md:text-4xl">{title}</h1>
          <p className="mt-2 max-w-2xl text-sm text-background-200 md:text-base">{description}</p>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-6 md:px-8">
        <Breadcrumb items={[{ label: title }]} />

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[240px_1fr] lg:gap-8">
          <aside className="hidden lg:block">
            <div className="sticky top-24 rounded-lg border border-background-200 bg-background-50 p-5">
              <div className="mb-5 flex items-center justify-between">
                <span className="flex items-center gap-1.5 font-heading text-base font-bold text-foreground-950">
                  <i className="ri-filter-3-line text-primary-600" aria-hidden="true" />
                  筛选
                </span>
                <button
                  type="button"
                  onClick={handleReset}
                  className="font-label text-xs text-foreground-500 transition-colors hover:text-accent-600 cursor-pointer"
                >
                  重置
                </button>
              </div>
              <FilterBar groups={groups} />
            </div>
          </aside>

          <div>
            <div className="mb-4 flex items-center justify-between gap-3">
              <p className="font-label text-sm text-foreground-600">
                共找到 <span className="font-semibold text-accent-600">{results.length}</span> 部作品
              </p>
              <button
                type="button"
                onClick={() => setPanelOpen((prev) => !prev)}
                className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-2 font-label text-sm text-foreground-800 lg:hidden cursor-pointer"
              >
                <i className="ri-filter-3-line" aria-hidden="true" />
                {panelOpen ? '收起筛选' : '筛选'}
              </button>
            </div>

            {panelOpen && (
              <div className="mb-5 rounded-lg border border-background-200 bg-background-50 p-5 lg:hidden">
                <div className="mb-4 flex items-center justify-between">
                  <span className="font-heading text-base font-bold text-foreground-950">筛选条件</span>
                  <button
                    type="button"
                    onClick={handleReset}
                    className="font-label text-xs text-foreground-500 cursor-pointer"
                  >
                    重置
                  </button>
                </div>
                <FilterBar groups={groups} />
              </div>
            )}

            {pageItems.length > 0 ? (
              <>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4">
                  {pageItems.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>
                <div className="mt-8">
                  <Pagination current={currentPage} totalPages={totalPages} onChange={setPage} />
                </div>
              </>
            ) : (
              <EmptyState
                icon="ri-emotion-sad-line"
                title="没有找到符合条件的作品"
                description="试试调整筛选条件，或重置后浏览全部内容。"
                action={
                  <button
                    type="button"
                    onClick={handleReset}
                    className="rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
                  >
                    重置筛选条件
                  </button>
                }
              />
            )}

            <div className="mt-6 rounded-lg border border-dashed border-background-300 bg-background-100 p-4 text-center">
              <p className="text-xs leading-relaxed text-foreground-600">
                <i className="ri-information-line mr-1" aria-hidden="true" />
                提示：本页筛选、排序与分页均为前端演示功能，数据为示范内容。
                <Link to="/about" className="ml-1 text-primary-600 hover:text-accent-600 cursor-pointer">
                  了解更多
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}